import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import DisplayAPIsException from "../../BasicComponents/DisplayAPIsExceptionAlert";
import { logEvent, logException } from "../../helper/AppInsightsHelper";
import { CustomShimmer } from "../../helper/CustomShimmer";
import { useGetCustomerEngagementsQuery } from "../../slices/ExploreCustomers.slice";
import CustomerEngagementChart from "./CustomerEngagementChart";
import CustomerEngagementTable from "./CustomerEngagementTable";
import AIInsightsButtonWithPanel from "../../helper/AIInsightsButtonWithPanel";

const CustomerEngagementContainer = (props: any) => {
  const [searchParams] = useSearchParams();
  const tenantId = searchParams.get("id");
  const { data, error, isFetching } = useGetCustomerEngagementsQuery(tenantId);
  const [filteredItems, setFilteredItems] = useState([]);

  // Change: chartFilter as array to handle multiple filters
  const [chartFilters, setChartFilters] = useState<
    Array<{
      filterKey: string;
      value: string;
    }>
  >([]);

  const HandleAPIExcpetion = () => (error && <DisplayAPIsException error={error} height={250} />) || "";

  const customProps = {
    tenantId: tenantId,
    tenantName: props?.tenantName
  };

  useEffect(() => {
    logEvent("Engagements Tab - Customer Engagements", "Customer Page", "CustomerEngagementsContainer", customProps);
  }, []);

  useEffect(() => {
    setFilteredItems(data?.engagementDetailsList ?? []);

    if (error) {
      const customProps = {
        tenantId: tenantId,
        tenantName: props?.tenantName
      };
      logException(
        "Customer Page - Customer Engagements Tab - Engagements Sub Tab",
        "CustomerEngagementContainer",
        "useGetCustomerEngagementsQuery",
        error,
        customProps
      );
    }
  }, [data, error]);

  useEffect(() => {
    const filteredData = (data?.engagementDetailsList || []).filter(({ engagementStatus, meetingType }: any) => {
      // If no filters, show all data
      if (chartFilters.length === 0) {
        return true;
      }

      // Apply all filters - item must match ALL active filters
      return chartFilters.every((filter) => {
        if (filter.filterKey === "engagementStatus") {
          return engagementStatus === filter.value;
        } else if (filter.filterKey === "meetingType") {
          return meetingType === filter.value;
        }
        return true;
      });
    });

    setFilteredItems(filteredData);
  }, [chartFilters, data]);

  // Handler to manage array of filters
  const handleChartFilter = (newFilter: { filterKey: string; value: string }) => {
    setChartFilters((prevFilters) => {
      // If filterKey or value is empty, reset all filters
      if (!newFilter.filterKey || !newFilter.value) {
        return [];
      }

      // Check if filter for this key already exists
      const existingFilterIndex = prevFilters.findIndex((f) => f.filterKey === newFilter.filterKey);

      if (existingFilterIndex === -1) {
        // Add new filter
        return [...prevFilters, newFilter];
      } else {
        // Update existing filter
        const updatedFilters = [...prevFilters];
        updatedFilters[existingFilterIndex] = newFilter;
        return updatedFilters;
      }
    });
  };

  const showNoData = () => {
    const rootStyle = {
      alignItems: "center",
      display: "flex",
      fontSize: 18,
      fontWeight: 600,
      justifyContent: "center",
      padding: 200
    };
    return <div style={rootStyle}>{`Engagements details are not available for ${props.tenantName}.`}</div>;
  };

  return (
    <>
      {data?.engagementDetailsList && data?.engagementDetailsList.length > 0 && (
        <AIInsightsButtonWithPanel
          metricId="5"
          panelTitle="Engagement Insights"
          tenantId={tenantId ?? ""}
          tenantName={props.tenantName}
        />
      )}
      <CustomerEngagementChart
        loading={isFetching}
        onSelectedChartFilter={handleChartFilter}
        options={{
          filteredData: filteredItems,
          originalData: data?.engagementDetailsList || [],
          chartOptions: [
            {
              id: 1,
              field: "meetingType",
              key: "customer-view-customer-engagements-engagements-chart-by-meetingType",
              title: "Meeting Type",
              type: "donut",
              kustoLink: data?.meetingTypeKustoLink
            },
            {
              id: 2,
              field: "engagementStatus",
              key: "customer-view-customer-engagements-engagements-chart-by-engagementStatus",
              title: "Engagement Status",
              type: "donut",
              kustoLink: data?.engagementStatusKustoLink
            }
          ]
        }}
      />
      {isFetching ? (
        <CustomShimmer columnCount={11} />
      ) : (
        (Array.isArray(data?.engagementDetailsList) && data?.engagementDetailsList.length && (
          <div style={{ marginLeft: 3, marginRight: 3 }}>
            <CustomerEngagementTable
              tableData={filteredItems}
              tenantName={props.tenantName}
              kustoLink={data?.engagementDetailsKustoLink}
            />
          </div>
        )) ||
        (Array.isArray(data?.engagementDetailsList) && data?.engagementDetailsList.length === 0 && showNoData())
      )}
      <HandleAPIExcpetion />
    </>
  );
};

export default CustomerEngagementContainer;
